import type { LandlordInterest, Tenant, VisitStatus } from "../models/tenant";
import type { Profile } from "../components/Profile/types";
import type { Color } from "../components/UI/Text";
// @ts-expect-error pure js
import { AncienneteStatus, StatusPro } from "../models/profile";

/** Loyer charges comprises de la candidature. */
export function getMonthlyRent(tenant: Pick<Tenant, "loyer_hc" | "charges">) {
  return (tenant.loyer_hc ?? 0) + (tenant.charges ?? 0);
}

/** Somme des revenus mensuels déclarés par les candidats (hors garants). */
export function getHouseholdIncome(profiles: Profile[]) {
  return profiles.reduce(
    (sum, profile) => sum + (profile.revenus_brut ?? 0),
    0,
  );
}

/**
 * Part du loyer charges comprises dans les revenus du foyer, en pourcentage.
 * `null` quand les revenus ne sont pas renseignés ou que le loyer est inconnu.
 */
export function getEffortRate(
  tenant: Pick<Tenant, "loyer_hc" | "charges">,
  profiles: Profile[],
): number | null {
  const rent = getMonthlyRent(tenant);
  const income = getHouseholdIncome(profiles);
  if (rent <= 0 || income <= 0) return null;
  return (rent / income) * 100;
}

/** Seuil usuel des bailleurs et assureurs : un tiers des revenus. */
const EFFORT_RATE_OK = 100 / 3;
const EFFORT_RATE_WARNING = 40;

export function getEffortRateColor(
  rate: number,
): "success" | "warning" | "danger" {
  if (rate <= EFFORT_RATE_OK) return "success";
  if (rate <= EFFORT_RATE_WARNING) return "warning";
  return "danger";
}

export function formatEffortRate(rate: number) {
  return `${rate.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`;
}

function getIncomePoints(income: number, rent: number) {
  if (income > rent * 3) return 5;
  if (income > rent * 2.5) return 4;
  if (income > rent * 2.3) return 3;
  if (income > rent * 2.1) return 2;
  return 1;
}

const STATUS_PRO_POINTS: Record<string, number> = {
  [StatusPro.CDI]: 5,
  [StatusPro.FONCTIONNAIRE]: 5,
  [StatusPro.RETRAITE]: 5,
  [StatusPro.INDEPENDANT]: 4,
  [StatusPro.CDD]: 3,
  [StatusPro.INTERIM]: 3,
  [StatusPro.SANSEMPLOI]: 1,
};

const ANCIENNETE_POINTS: Record<string, number> = {
  [AncienneteStatus.YEAR2PLUS]: 5,
  [AncienneteStatus.YEAR2]: 4,
  [AncienneteStatus.YEAR1]: 3,
  [AncienneteStatus.MONTH6]: 2,
  [AncienneteStatus.MONTH3]: 1,
};

function getSituationPoints(profile: Profile) {
  return (
    (STATUS_PRO_POINTS[profile.status_pro ?? ""] ?? 0) +
    (ANCIENNETE_POINTS[profile.anciennete ?? ""] ?? 0)
  );
}

/** Points d'un garant pris isolément : ses revenus face au loyer, sa situation. */
function getGarantorPoints(garantor: Profile, rent: number) {
  return (
    getIncomePoints(garantor.revenus_brut ?? 0, rent) +
    getSituationPoints(garantor)
  );
}

function getBestGarantorPoints(garantors: Profile[], rent: number) {
  return garantors.reduce(
    (best, garantor) => Math.max(best, getGarantorPoints(garantor, rent)),
    0,
  );
}

const COVERED_GUARANTEE_POINTS = 5;

/** Bonus apporté par la garantie du candidat (caution physique, Visale, assurance). */
function getGuaranteePoints(profile: Profile, rent: number) {
  const garantors = profile.garantors ?? [];
  switch (profile.caution_type) {
    case "Visale":
      return (
        COVERED_GUARANTEE_POINTS +
        (profile.status_pro === StatusPro.ETUDIANT ? 3 : 0)
      );
    case "GLI":
    case "Autre":
      return COVERED_GUARANTEE_POINTS;
    case "Physique": {
      if (garantors.length === 0) return 1;
      const best = getBestGarantorPoints(garantors, rent);
      if (best > 13) return 5;
      if (best > 11) return 4;
      if (best > 9) return 3;
      if (best > 6) return 2;
      return 1;
    }
    default:
      return 1;
  }
}

function average(values: number[]) {
  return values.length === 0
    ? 0
    : values.reduce((sum, value) => sum + value, 0) / values.length;
}

interface ConfidenceInput {
  tenant: Pick<Tenant, "loyer_hc" | "charges">;
  /** Candidats du dossier, hors garants. */
  profiles: Profile[];
  /** Bien couvert par une assurance loyers impayés : la garantie du candidat n'entre plus en compte. */
  propertyGli?: boolean;
}

/**
 * Note de confiance sur 5 du dossier : revenus du foyer face au loyer, situation
 * professionnelle et ancienneté de chaque candidat, et garantie apportée.
 * Un étudiant avec caution physique est évalué sur la situation de son meilleur garant.
 */
export function getConfidenceNote({
  tenant,
  profiles,
  propertyGli = false,
}: ConfidenceInput): number {
  const rent = getMonthlyRent(tenant);
  const householdIncome = getHouseholdIncome(profiles);

  const basePoints = average(
    profiles.map((profile) => {
      const garantors = profile.garantors ?? [];
      const studentWithGarantor =
        profile.status_pro === StatusPro.ETUDIANT &&
        profile.caution_type === "Physique" &&
        garantors.length > 0;
      if (studentWithGarantor && !propertyGli) {
        return getBestGarantorPoints(garantors, rent);
      }
      return (
        getIncomePoints(householdIncome, rent) + getSituationPoints(profile)
      );
    }),
  );

  const guaranteePoints = propertyGli
    ? COVERED_GUARANTEE_POINTS
    : Math.max(
        ...profiles.map((profile) => getGuaranteePoints(profile, rent)),
        1,
      );

  const points = basePoints + guaranteePoints;
  if (points > 17) return 5;
  if (points > 14) return 4;
  if (points > 11) return 3;
  if (points > 9) return 2;
  return 1;
}

interface LabeledOption<T extends string> {
  value: T | "";
  label: string;
  color: Color;
}

export const LANDLORD_INTEREST_OPTIONS: LabeledOption<LandlordInterest>[] = [
  { value: "", label: "Sans avis", color: "neutral" },
  { value: "to_review", label: "À étudier", color: "warning" },
  { value: "interesting", label: "Intéressant", color: "success" },
  { value: "rejected", label: "Non retenu", color: "danger" },
];

/** Champs de suivi d'une candidature, privés au bailleur. */
export type FollowUp = Pick<
  Tenant,
  "landlord_interest" | "visit_status" | "visit_date"
>;

/** Valeur acceptée par un `<input type="datetime-local">` : minutes incluses, sans secondes. */
export function toDateTimeLocal(value: string | null | undefined) {
  return value ? value.slice(0, 16) : "";
}

export const VISIT_STATUS_OPTIONS: LabeledOption<VisitStatus>[] = [
  { value: "", label: "Aucune visite", color: "neutral" },
  { value: "to_schedule", label: "À programmer", color: "warning" },
  { value: "scheduled", label: "Programmée", color: "info" },
  { value: "done", label: "Réalisée", color: "success" },
  { value: "cancelled", label: "Annulée", color: "danger" },
];

export function getLandlordInterestOption(
  value: LandlordInterest | null | undefined,
) {
  return (
    LANDLORD_INTEREST_OPTIONS.find(
      (option) => option.value === (value ?? ""),
    ) ?? LANDLORD_INTEREST_OPTIONS[0]
  );
}

export function getVisitStatusOption(value: VisitStatus | null | undefined) {
  return (
    VISIT_STATUS_OPTIONS.find((option) => option.value === (value ?? "")) ??
    VISIT_STATUS_OPTIONS[0]
  );
}
